import {
  DATABASE_MODES,
  fetchStudyChart,
  getApiUrl,
  getDatabaseMode,
  saveStudyChart,
} from '../api';
import {
  applyProjection,
  emptyAllianceState,
  emptyCompassState,
  overlayFromOther,
} from './sharedConcepts';

const memory = new Map();
const EXAMPLE_KEY = 'study-chart-v1:example';

export function patientChartId(demo, patientId) {
  if (!demo) return 'example';
  if (patientId == null || patientId === '') return '';
  return `patient:${patientId}`;
}

function emptyChart() {
  return {
    compass: null,
    alliance: null,
    compassTouched: false,
    allianceTouched: false,
    revision: 0,
  };
}

function readChart(id) {
  if (!id) return emptyChart();
  if (id === 'example') {
    try {
      const saved = JSON.parse(localStorage.getItem(EXAMPLE_KEY) || '');
      if (saved && typeof saved === 'object') {
        return {
          compass: saved.compass || null,
          alliance: saved.alliance || null,
          compassTouched: Boolean(saved.compassTouched),
          allianceTouched: Boolean(saved.allianceTouched),
        };
      }
    } catch {
      /* Fall through to the previous per-study keys. */
    }
    const chart = emptyChart();
    try {
      chart.compass = JSON.parse(localStorage.getItem('compass-example-v1') || '') || null;
    } catch {
      chart.compass = null;
    }
    try {
      chart.alliance = JSON.parse(localStorage.getItem('alliance-example-v1') || '') || null;
    } catch {
      chart.alliance = null;
    }
    return chart;
  }
  return memory.get(id) || emptyChart();
}

function writeChart(id, chart) {
  if (!id) return;
  if (id === 'example') {
    localStorage.setItem(EXAMPLE_KEY, JSON.stringify(chart));
    return;
  }
  chart.revision = (Number(chart.revision) || 0) + 1;
  memory.set(id, chart);
  const patientId = patientIdFromChartId(id);
  if (patientId && hydratedPatients.has(patientId)) scheduleDemoChartSave(patientId);
}

export function loadStudy(id, study) {
  return readChart(id)[study] || null;
}

export function studyWasOpened(id, study) {
  return Boolean(readChart(id)[`${study}Touched`]);
}

export function savedDemographicsBlank(saved, study) {
  const forms = saved?.forms || {};
  if (study === 'alliance') {
    const ethnicity = forms['ethnicity::case']?.values || {};
    const baseline = forms['baseline::case']?.values || {};
    const shown = (key) => ethnicity[`${key}#0`] || ethnicity[key];
    return !shown('alliance.ethnicity')
      && !shown('alliance.race')
      && !baseline['alliance.baseline.sex_at_birth']
      && !baseline['alliance.baseline.age'];
  }
  const demo = forms['demographics::pre-op']?.values || {};
  const race = demo.race;
  return !demo.ethnicity && !demo.sex && !demo.age_at_surgery && !(Array.isArray(race) && race.length);
}

export function saveStudy(id, study, state, { touch = false } = {}) {
  if (!id || !state) return;
  const chart = readChart(id);
  chart[study] = state;
  if (touch) chart[`${study}Touched`] = true;
  writeChart(id, chart);
}

export function commitLinkedEdit(id, study, state, fieldKey, formInstanceKey) {
  const otherName = study === 'compass' ? 'alliance' : 'compass';
  const result = applyProjection(study, state, fieldKey, formInstanceKey, loadStudy(id, otherName));
  if (id && result.other) saveStudy(id, otherName, result.other);
  if (id) saveStudy(id, study, result.self, { touch: true });
  return result.self;
}

export function adoptStudy(id, study, state) {
  const otherName = study === 'compass' ? 'alliance' : 'compass';
  const next = overlayFromOther(study, state, otherName, loadStudy(id, otherName));
  if (id) saveStudy(id, study, next, { touch: true });
  return next;
}

export function broadcastStudy(id, study, state) {
  const otherName = study === 'compass' ? 'alliance' : 'compass';
  const base = loadStudy(id, otherName) || (otherName === 'compass' ? emptyCompassState() : emptyAllianceState());
  const other = overlayFromOther(otherName, base, study, state);
  if (id) {
    saveStudy(id, otherName, other);
    saveStudy(id, study, state, { touch: true });
  }
  return state;
}

export function clearExampleChart() {
  localStorage.removeItem(EXAMPLE_KEY);
  localStorage.removeItem('compass-example-v1');
  localStorage.removeItem('alliance-example-v1');
}

export function overlaySavedAnswers(fresh, saved) {
  if (!saved?.forms) return fresh;
  const forms = { ...(fresh?.forms || {}) };
  Object.entries(saved.forms).forEach(([id, form]) => {
    if (!form?.values) return;
    const prior = forms[id] || { values: {}, repeats: {} };
    const values = { ...(prior.values || {}) };
    Object.entries(form.values).forEach(([key, value]) => {
      if (filledAnswer(value)) values[key] = value;
    });
    forms[id] = {
      ...prior,
      values,
      repeats: { ...(prior.repeats || {}), ...(form.repeats || {}) },
    };
  });
  const next = {
    ...(fresh || {}),
    forms,
    active: saved.active || fresh?.active || null,
  };
  if (Object.prototype.hasOwnProperty.call(saved, 'arm')) next.arm = saved.arm;
  if (Object.prototype.hasOwnProperty.call(saved, 'region')) next.region = saved.region;
  if (saved.visits && typeof saved.visits === 'object') next.visits = saved.visits;
  if (saved.phases && typeof saved.phases === 'object') next.phases = saved.phases;
  return next;
}

function filledAnswer(value) {
  if (value === true) return true;
  if (Array.isArray(value)) return value.length > 0;
  return value != null && value !== '' && value !== false;
}

const hydratedPatients = new Set();
const hydrateRequests = new Map();
const completedSubsections = new Map();
const saveTimers = new Map();
const dirtyPatients = new Set();
const latestSave = new Map();
const saveChain = new Map();
const saveAbort = new Map();
const chartSavedListeners = new Set();

export function onDemoChartSaved(listener) {
  chartSavedListeners.add(listener);
  return () => chartSavedListeners.delete(listener);
}

function notifyDemoChartSaved(patientId, detail = {}) {
  chartSavedListeners.forEach((listener) => {
    try {
      listener(patientId, detail);
    } catch (error) {
      // A list refresh should not block the chart save.
    }
  });
}

function patientIdFromChartId(id) {
  return id && id.startsWith('patient:') ? id.slice('patient:'.length) : '';
}

export function demoChartsUseDatabase() {
  try {
    const mode = typeof getDatabaseMode === 'function' ? getDatabaseMode() : '';
    return mode === DATABASE_MODES.DEMO || mode === DATABASE_MODES.LIVE;
  } catch (error) {
    return false;
  }
}

function installLoadedChart(patientId, payload) {
  if (!payload || typeof payload !== 'object') return;
  const id = patientChartId(true, patientId);
  const chart = readChart(id);
  if (payload.compass) {
    chart.compass = chart.compass ? overlaySavedAnswers(payload.compass, chart.compass) : payload.compass;
  }
  if (payload.alliance) {
    chart.alliance = chart.alliance ? overlaySavedAnswers(payload.alliance, chart.alliance) : payload.alliance;
  }
  if (payload.compassTouched) chart.compassTouched = true;
  if (payload.allianceTouched) chart.allianceTouched = true;
  const serverRevision = Number(payload.revision) || 0;
  const localRevision = Number(chart.revision) || 0;
  chart.revision = Math.max(localRevision, serverRevision);
  memory.set(id, chart);
}

export function hydrateDemoChart(patientId) {
  if (!demoChartsUseDatabase() || !patientId) return Promise.resolve(null);
  if (hydrateRequests.has(patientId)) return hydrateRequests.get(patientId);
  const pending = (async () => {
    try {
      const payload = await fetchStudyChart(patientId);
      installLoadedChart(patientId, payload);
      return payload;
    } catch (error) {
      console.error('Study chart load failed', error);
      return null;
    } finally {
      hydratedPatients.add(patientId);
      hydrateRequests.delete(patientId);
    }
  })();
  hydrateRequests.set(patientId, pending);
  return pending;
}

function chartPayload(patientId) {
  const chart = readChart(patientChartId(true, patientId));
  if (!chart.compass && !chart.alliance) return null;
  return {
    compass: chart.compass,
    alliance: chart.alliance,
    compassTouched: Boolean(chart.compassTouched),
    allianceTouched: Boolean(chart.allianceTouched),
    revision: Number(chart.revision) || 0,
  };
}

function freezeChartPayload(patientId) {
  const body = chartPayload(patientId);
  if (!body) return null;
  return JSON.parse(JSON.stringify(body));
}

function isAbortError(error) {
  return error?.name === 'AbortError';
}

function abortInFlight(patientId) {
  const controller = saveAbort.get(patientId);
  if (controller) controller.abort();
}

function keepaliveSave(patientId, body) {
  try {
    const token = localStorage.getItem('authToken');
    if (!token || typeof fetch !== 'function') return;
    fetch(`${getApiUrl()}/patients/${encodeURIComponent(patientId)}/study-chart`, {
      method: 'PUT',
      keepalive: true,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'X-Database': getDatabaseMode(),
      },
      body: JSON.stringify(body),
    }).catch(() => {});
  } catch (error) {
    // The page is going away; the in-memory chart is the copy we still have.
  }
}

function currentRevision(patientId) {
  return Number(readChart(patientChartId(true, patientId)).revision) || 0;
}

function settleRevision(patientId, revision) {
  if (currentRevision(patientId) <= revision) dirtyPatients.delete(patientId);
}

function sendDemoChart(patientId, { keepalive = false } = {}) {
  if (!demoChartsUseDatabase() || !patientId || !hydratedPatients.has(patientId)) {
    return Promise.resolve(null);
  }
  const body = freezeChartPayload(patientId);
  if (!body) {
    dirtyPatients.delete(patientId);
    return Promise.resolve(null);
  }
  if (keepalive) {
    latestSave.set(patientId, body);
    abortInFlight(patientId);
    keepaliveSave(patientId, body);
    return Promise.resolve(body);
  }
  latestSave.set(patientId, body);
  abortInFlight(patientId);
  if (saveChain.has(patientId)) return saveChain.get(patientId);
  const chain = (async () => {
    try {
      while (latestSave.has(patientId)) {
        const next = latestSave.get(patientId);
        latestSave.delete(patientId);
        const controller = new AbortController();
        saveAbort.set(patientId, controller);
        const revision = Number(next.revision) || 0;
        try {
          const result = await saveStudyChart(patientId, next, { signal: controller.signal });
          if (controller.signal.aborted) continue;
          if (!result?.skipped) {
            ['alliance', 'compass'].forEach((study) => {
              if (next?.[study]) {
                notifyDemoChartSaved(patientId, { saved: true, study, state: next[study] });
              }
            });
          }
          settleRevision(patientId, revision);
        } catch (error) {
          if (isAbortError(error) || controller.signal.aborted) continue;
          console.error('Study chart save failed', error);
          scheduleDemoChartSave(patientId);
          break;
        } finally {
          if (saveAbort.get(patientId) === controller) saveAbort.delete(patientId);
        }
      }
    } finally {
      saveChain.delete(patientId);
      if (latestSave.has(patientId)) sendDemoChart(patientId);
    }
  })();
  saveChain.set(patientId, chain);
  return chain;
}

function scheduleDemoChartSave(patientId) {
  if (!demoChartsUseDatabase() || !hydratedPatients.has(patientId)) return;
  dirtyPatients.add(patientId);
  clearTimeout(saveTimers.get(patientId));
  saveTimers.set(patientId, setTimeout(() => {
    saveTimers.delete(patientId);
    sendDemoChart(patientId);
  }, 500));
}

function noteCompletedSubsections(patientId, study, sectionIds) {
  if (!Array.isArray(sectionIds)) return false;
  const watchKey = `${patientId}:${study}`;
  const next = new Set(sectionIds);
  const previous = completedSubsections.get(watchKey);
  completedSubsections.set(watchKey, next);
  if (!previous) return false;
  for (const id of next) {
    if (!previous.has(id)) return true;
  }
  return false;
}

export function persistDemoStudy(patientId, study, state, completedSections = null) {
  if (!demoChartsUseDatabase() || !patientId || !state || !hydratedPatients.has(patientId)) return undefined;
  saveStudy(patientChartId(true, patientId), study, state, { touch: true });
  if (study === 'alliance' || study === 'compass') {
    notifyDemoChartSaved(patientId, { study, state });
  }
  if (noteCompletedSubsections(patientId, study, completedSections)) {
    return flushDemoChart(patientId);
  }
  return undefined;
}

export function flushDemoChart(patientId, options) {
  if (!patientId) return Promise.resolve(null);
  clearTimeout(saveTimers.get(patientId));
  saveTimers.delete(patientId);
  return sendDemoChart(patientId, options);
}

export function flushOpenDemoCharts({ keepalive = false } = {}) {
  return Promise.all([...dirtyPatients].map((patientId) => flushDemoChart(patientId, { keepalive })));
}

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => {
    flushOpenDemoCharts({ keepalive: true });
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushOpenDemoCharts();
  });
}
